import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, Plus, Trash, CheckCircle, XCircle, HardDrives, ShieldCheck } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#14B8A6';

const RDPAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ label: '', tenant: '', client_id: '', client_secret: '', subscription_id: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');

  const load = async () => {
    try { const r = await axios.get(`${API}/api/rdp/accounts`, { withCredentials: true }); setAccounts(r.data); }
    catch { toast.error('Failed to load accounts'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.label || !form.tenant || !form.client_id || !form.client_secret || !form.subscription_id) return toast.error('Fill all fields');
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/rdp/accounts`, form, { withCredentials: true });
      toast.success('Account added — testing…');
      setShow(false); setForm({ label: '', tenant: '', client_id: '', client_secret: '', subscription_id: '' });
      await load();
      test(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try { const r = await axios.post(`${API}/api/rdp/accounts/${id}/test`, {}, { withCredentials: true }); toast.success(`Ready — ${r.data.resource_groups} resource groups, providers OK`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); load(); }
    finally { setTesting(''); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this Azure account from the app?')) return;
    try { await axios.delete(`${API}/api/rdp/accounts/${id}`, { withCredentials: true }); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const fld = (k, label, ph, type = 'text') => (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-1">{label}</label>
      <input type={type} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
        data-testid={`rdp-acc-${k}`} className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white text-sm focus:outline-none focus:border-[#14B8A6]" />
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black mb-2" style={{ color: T }}>AZURE ACCOUNTS</h1>
          <p className="text-slate-400 font-mono">Onboard an Azure account via its service principal (Tenant, App ID, Secret, Subscription).</p>
        </div>
        <button onClick={() => setShow(!show)} data-testid="rdp-add-account-btn"
          className="flex items-center gap-2 text-black px-4 py-2 font-bold uppercase tracking-wider" style={{ background: T }}>
          <Plus size={20} /> Add Account
        </button>
      </div>

      {show && (
        <div className="bg-card border p-6 max-w-2xl space-y-4" style={{ borderColor: T + '55' }}>
          {fld('label', 'Nickname', 'e.g. Main RDP Azure')}
          {fld('subscription_id', 'Subscription ID', 'ccc70b78-...')}
          {fld('tenant', 'Tenant ID', 'f2163463-...')}
          {fld('client_id', 'App ID (Client ID)', 'e8247fc4-...')}
          {fld('client_secret', 'Client Secret (Password)', '••••••', 'password')}
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} data-testid="rdp-save-account-btn"
              className="flex items-center gap-2 text-black px-5 py-3 font-bold uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>
              {saving ? <CircleNotch size={18} className="animate-spin" /> : <ShieldCheck size={18} />} Save & Test
            </button>
            <button onClick={() => setShow(false)} className="border border-white/15 text-slate-300 px-5 py-3 font-bold uppercase tracking-wider text-sm hover:bg-white/5">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {accounts.map(a => (
          <div key={a.id} className="bg-card border border-white/10 p-6" data-testid={`rdp-account-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <HardDrives size={24} style={{ color: T }} />
                <div>
                  <div className="font-bold text-white">{a.label}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.subscription_id}</div>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-bold border ${a.status === 'ready' ? 'text-[#14B8A6] border-[#14B8A6]/40' : a.status === 'error' ? 'text-red-400 border-red-400/40' : 'text-slate-400 border-white/10'}`}>{a.status}</span>
            </div>
            {a.provider_status && Object.keys(a.provider_status).length > 0 && (
              <div className="mt-3 flex gap-3 text-xs">
                {Object.entries(a.provider_status).map(([k, v]) => (
                  <span key={k} className="flex items-center gap-1 text-slate-400">
                    {v === 'Registered' ? <CheckCircle size={13} className="text-[#14B8A6]" /> : <XCircle size={13} className="text-amber-400" />} {k.replace('Microsoft.', '')}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={() => test(a.id)} disabled={testing === a.id} data-testid={`rdp-test-${a.id}`}
                className="flex items-center gap-2 border border-[#14B8A6]/40 text-[#14B8A6] px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-[#14B8A6] hover:text-black transition-colors">
                {testing === a.id ? <CircleNotch size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Test
              </button>
              <button onClick={() => del(a.id)} data-testid={`rdp-del-${a.id}`}
                className="flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-red-500 hover:text-white transition-colors">
                <Trash size={14} /> Remove
              </button>
              <CopyApiButton provider="rdp" accountId={a.id} />
            </div>
          </div>
        ))}
        {accounts.length === 0 && <div className="text-slate-500 md:col-span-2 text-center py-10">No Azure accounts yet. Add one to start.</div>}
      </div>
    </div>
  );
};

export default RDPAccounts;
